import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { SYMBOL_DATA, INTERVALS, PERIODS } from '../constants/symbols';
import { ETF_SYMBOL_DATA } from '../constants/etfs';
import ScheduleConfigurationFields, { isScheduleConfigurationValid, scheduleConfiguration, SCHEDULE_TIMEZONE } from './ScheduleConfigurationFields';

const parseSymbols = value => [...new Set(value.toUpperCase().split(/[,\s]+/).filter(Boolean))];
const actionButtonClass = 'inline-flex min-h-10 items-center justify-center rounded-lg border px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';
const settingsDefaults = {
  period: '5d', interval: '5m', include_extended_hours: false,
  lookback: null, account_size: 10000, risk_pct: 1, timeout: 8, max_retries: 3,
};

export default function ScanScheduleModal({ schedule, onClose, onSave, onDelete, onCancel, onRun }) {
  const { t, i18n } = useTranslation();
  const [configuration, setConfiguration] = useState(() => scheduleConfiguration(schedule));
  const [settings, setSettings] = useState(() => ({ ...settingsDefaults, ...schedule.settings }));
  const [symbols, setSymbols] = useState(() => parseSymbols(schedule.symbols.join(' ')));
  const [pendingSymbols, setPendingSymbols] = useState([]);
  const [symbolSearch, setSymbolSearch] = useState('');
  const [symbolUniverse, setSymbolUniverse] = useState('sp500');
  const [savedSymbolOptions] = useState(() => {
    const catalogSymbols = new Set([...SYMBOL_DATA, ...ETF_SYMBOL_DATA].map(item => item.symbol));
    const savedSymbols = parseSymbols(schedule.symbols.join(' '))
      .filter(symbol => !catalogSymbols.has(symbol))
      .map(symbol => ({ symbol, name: '' }));
    return savedSymbols;
  });
  const symbolOptions = [...(symbolUniverse === 'etf' ? ETF_SYMBOL_DATA : SYMBOL_DATA), ...savedSymbolOptions];
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const cancelling = schedule.last_status === 'cancelling';
  const running = schedule.last_status === 'running' || cancelling;
  const disabled = busy || running;
  const searchTerms = symbolSearch.toLowerCase().split(',').map(term => term.trim()).filter(Boolean);
  const filteredSymbols = symbolOptions.filter(item => !searchTerms.length || searchTerms.some(term =>
    item.symbol.toLowerCase().includes(term) || item.name.toLowerCase().includes(term)
  ));
  const selectedSymbols = new Set(pendingSymbols);
  const allFilteredSelected = filteredSymbols.length > 0 && filteredSymbols.every(item => selectedSymbols.has(item.symbol));
  const someFilteredSelected = !allFilteredSelected && filteredSymbols.some(item => selectedSymbols.has(item.symbol));
  const toggleSymbol = symbol => setPendingSymbols(previous => previous.includes(symbol)
    ? previous.filter(item => item !== symbol)
    : [...previous, symbol]);
  const toggleFilteredSymbols = () => {
    const filtered = filteredSymbols.map(item => item.symbol);
    const filteredSet = new Set(filtered);
    setPendingSymbols(previous => filtered.every(symbol => previous.includes(symbol))
      ? previous.filter(symbol => !filteredSet.has(symbol))
      : [...new Set([...previous, ...filtered])]);
  };
  const canAddSymbols = pendingSymbols.some(symbol => !symbols.includes(symbol));
  const addSymbols = () => {
    if (disabled || !canAddSymbols) return;
    setSymbols(previous => [...new Set([...previous, ...pendingSymbols])]);
    setPendingSymbols([]);
  };
  const removeSymbol = symbol => setSymbols(previous => previous.filter(item => item !== symbol));
  const validSymbols = symbols.length > 0 && symbols.length <= 506 && symbols.every(symbol => /^[A-Z0-9.^=\-]{1,20}$/.test(symbol));
  const periodMatch = /^(\d+)(d|wk|mo|y)$/.exec(settings.period);
  const periodDays = periodMatch ? Number(periodMatch[1]) * { d: 1, wk: 7, mo: 30, y: 365 }[periodMatch[2]] : Infinity;
  const incompatiblePeriod = settings.interval === '1m' && periodDays > 7;
  const validSettings = (settings.lookback === null || (Number.isInteger(settings.lookback) && settings.lookback > 0)) &&
    settings.account_size > 0 && settings.risk_pct > 0 && settings.risk_pct <= 100 &&
    Number.isInteger(settings.timeout) && settings.timeout >= 1 && settings.timeout <= 30 &&
    Number.isInteger(settings.max_retries) && settings.max_retries >= 1 && settings.max_retries <= 10;

  const perform = async operation => {
    setBusy(true);
    setError('');
    try {
      await operation();
      onClose();
    } catch (failure) {
      const detail = failure.response?.data?.detail;
      setError(Array.isArray(detail) ? detail.map(item => item.msg).join('; ') : detail || failure.message);
    } finally {
      setBusy(false);
    }
  };

  const save = event => {
    event.preventDefault();
    if (busy || running || !validSymbols || !validSettings || incompatiblePeriod || !isScheduleConfigurationValid(configuration)) return;
    perform(() => onSave(schedule.id, { ...configuration, symbols, settings }));
  };
  const remove = () => {
    if (!busy && !running && window.confirm(t('schedule_delete_confirm', { defaultValue: 'Delete this schedule?' }))) {
      perform(() => onDelete(schedule.id));
    }
  };
  const cancelRun = () => {
    if (!busy && running && !cancelling && window.confirm(t('schedule_cancel_confirm', { defaultValue: 'Cancel this run and pause future runs? Active symbol scans may finish. Saved results will be kept.' }))) {
      perform(() => onCancel(schedule.id));
    }
  };
  const dateLabel = value => value ? new Date(value).toLocaleString(i18n?.language, { timeZone: SCHEDULE_TIMEZONE }) : '-';
  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg';
  const summary = schedule.last_summary || {};
  const progressTotal = Number(summary.total ?? schedule.symbols.length ?? 0);
  const progressScanned = Number(summary.scanned ?? 0);
  const progressErrors = Array.isArray(summary.errors) ? summary.errors.length : 0;
  const progressProcessedRaw = Number(summary.processed ?? (progressScanned + progressErrors));
  const progressProcessed = progressTotal > 0 ? Math.max(0, Math.min(progressProcessedRaw, progressTotal)) : Math.max(0, progressProcessedRaw);
  return (
    <Dialog.Root open onOpenChange={open => { if (!open && !busy) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-[60]" />
        <Dialog.Content aria-describedby={undefined} className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[70] flex max-h-[90vh] w-[calc(100%_-_2rem)] max-w-6xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
          <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b bg-white px-6 py-4">
            <Dialog.Title className="flex items-center gap-2 text-xl font-semibold">
              <span>{t('auto_scanner_settings', { defaultValue: 'Auto Scanner Settings' })}</span>
              {['running', 'cancelling'].includes(schedule.last_status) && progressTotal > 0 && <span className="text-sm font-medium text-blue-700">({progressProcessed}/{progressTotal})</span>}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button type="button" className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 disabled:opacity-50" aria-label={t('schedule_close', { defaultValue: 'Close schedule details' })} disabled={busy}>
                <svg aria-hidden="true" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="m6 6 12 12M18 6 6 18" />
                </svg>
              </button>
            </Dialog.Close>
          </div>
          <div className="overflow-y-auto px-6 py-4">
          {cancelling && <p role="status" className="mb-4 text-sm text-amber-800">{t('schedule_cancelling', { defaultValue: 'Cancellation requested. Waiting for active symbol scans to finish. Future runs are paused.' })}</p>}
          <div className="mb-4 space-y-1 text-sm text-gray-600">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <p className="whitespace-nowrap"><span className="font-semibold">{t('schedule_next', { defaultValue: 'Next run' })} (NY):</span> {dateLabel(schedule.next_run_at)}</p>
              <p className="whitespace-nowrap"><span className="font-semibold">{t('schedule_last', { defaultValue: 'Last run' })} (NY):</span> {dateLabel(schedule.last_run_at)} {schedule.last_status && '(' + schedule.last_status + ')'}</p>
            </div>
            {schedule.last_summary && <p>{t('schedule_summary', { defaultValue: '{{scanned}} scanned, {{signals}} signals, {{errors}} errors', scanned: schedule.last_summary.scanned, signals: schedule.last_summary.signals, errors: schedule.last_summary.errors.length })}</p>}
            {schedule.last_summary?.errors?.length > 0 && <div role="region" aria-label={t('schedule_errors', { defaultValue: 'Scan errors' })} tabIndex={0} className="max-h-48 overflow-y-auto overscroll-contain space-y-1 rounded-lg border border-red-200 bg-red-50 p-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600">
              {schedule.last_summary.errors.map((failure, index) => <p key={index} className="whitespace-pre-wrap break-words text-red-700">{failure.symbol}: {failure.error}</p>)}
            </div>}
          </div>
          <form onSubmit={save} className="space-y-4">
            <ScheduleConfigurationFields configuration={configuration} onChange={setConfiguration} disabled={disabled} />
            <fieldset disabled={disabled} className="space-y-4 min-w-0">

              <div className="min-w-0 space-y-2">
                <div role="group" aria-label={t('scanner_symbol_universe', { defaultValue: 'Symbol universe' })} className="flex rounded-lg border border-gray-300 p-1">
                  {[['sp500', 'S&P 500'], ['etf', 'ETF']].map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      aria-pressed={symbolUniverse === value}
                      onClick={() => setSymbolUniverse(value)}
                      className={`min-h-8 flex-1 rounded-md px-2 py-1 text-xs font-medium transition ${symbolUniverse === value ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-blue-50'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <input
                  type="search"
                  name="symbol_search"
                  aria-label={t('schedule_search_symbols', { defaultValue: 'Search symbols' })}
                  placeholder={t('search_by_symbol', { defaultValue: 'Search Symbols e.g., NVDA, AAPL' })}
                  value={symbolSearch}
                  onChange={event => setSymbolSearch(event.target.value)}
                  className={`${inputClass} focus:ring-2 focus:ring-blue-500`}
                />
                <div role="group" aria-label={t('schedule_symbols', { defaultValue: 'Symbols' })} className="max-h-[94px] overflow-y-auto overscroll-contain rounded-lg border border-gray-300">
                  <div className="sticky top-0 z-10 border-b bg-gray-50 px-2 text-xs">
                    <label className="flex min-h-7 w-full cursor-pointer items-center gap-2">
                      <input
                        type="checkbox"
                        checked={allFilteredSelected}
                        ref={element => { if (element) element.indeterminate = someFilteredSelected; }}
                        onChange={toggleFilteredSymbols}
                        disabled={!filteredSymbols.length}
                        aria-label={allFilteredSelected ? t('deselect_all', { defaultValue: 'Deselect all' }) : t('select_all', { defaultValue: 'Select all' })}
                        className="h-3.5 w-3.5 shrink-0 cursor-pointer accent-blue-600"
                      />
                      <span className="font-medium text-gray-700">{allFilteredSelected ? t('deselect_all', { defaultValue: 'Deselect all' }) : t('select_all', { defaultValue: 'Select all' })}</span>
                      <span className="ml-auto text-gray-600">{t('symbols_found_count', { count: filteredSymbols.length, defaultValue: `${filteredSymbols.length} symbols found` })}</span>
                    </label>
                  </div>
                  {filteredSymbols.map(item => (
                    <label key={item.symbol} className={`flex min-h-6 cursor-pointer items-center gap-1.5 px-2 py-1 transition hover:bg-blue-50 ${selectedSymbols.has(item.symbol) ? 'bg-blue-50' : ''}`}>
                      <input
                        type="checkbox"
                        value={item.symbol}
                        checked={selectedSymbols.has(item.symbol)}
                        onChange={() => toggleSymbol(item.symbol)}
                        className="h-3 w-3 shrink-0 cursor-pointer accent-blue-600"
                      />
                      <span className={`text-xs font-medium ${selectedSymbols.has(item.symbol) ? 'text-blue-600' : 'text-gray-800'}`}>{item.symbol}</span>
                      <span className="ml-auto min-w-0 break-words pl-2 text-right text-[11px] text-gray-500">{item.name || t('schedule_saved_symbol', { defaultValue: 'Saved symbol' })}</span>
                    </label>
                  ))}
                  {!filteredSymbols.length && <p className="px-3 py-4 text-center text-sm text-gray-500">{t('no_symbols_found', { defaultValue: 'No symbols found' })}</p>}
                </div>
                <button type="button" onClick={addSymbols} disabled={disabled || !canAddSymbols} className="rounded-lg border border-blue-600 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400">
                  {t('schedule_add_symbols', { defaultValue: 'Add symbols' })}
                </button>
                <p className="text-sm text-gray-600">{t('schedule_symbol_count', { defaultValue: 'Symbols' })}: {symbols.length}</p>
                <div role="group" aria-label={t('schedule_selected_symbols', { defaultValue: 'Selected symbols' })} className="max-h-48 overflow-y-auto overscroll-contain rounded-lg border border-gray-200 p-2">
                  {symbols.length > 0 ? <div className="flex flex-wrap gap-2">
                    {symbols.map(symbol => <span key={symbol} className="inline-flex items-center gap-1 rounded-full border border-gray-300 bg-gray-50 px-2 py-1 text-xs text-gray-700">
                      <span>{symbol}</span>
                      <button type="button" onClick={() => removeSymbol(symbol)} disabled={disabled} aria-label={`${t('remove', { defaultValue: 'Remove' })} ${symbol}`} className="rounded-full px-1 text-gray-500 hover:bg-gray-200 hover:text-gray-700 disabled:opacity-50">×</button>
                    </span>)}
                  </div> : <p className="text-sm text-gray-500">{t('schedule_no_symbols_selected', { defaultValue: 'No symbols selected' })}</p>}
                </div>
              </div>
              {!validSymbols && <p className="text-sm text-red-700">{t('schedule_invalid_symbols', { defaultValue: 'Select between 1 and 506 valid ticker symbols.' })}</p>}
              <div className="grid grid-cols-1 sm:grid-cols-3 items-end gap-2">
                {[['period', PERIODS], ['interval', INTERVALS]].map(([field, choices]) => (
                  <label key={field} className="block min-w-0 text-xs font-medium">
                    <span className="mb-2 block leading-5">{t(field)}</span>
                    <select name={field} className="w-full min-w-0 h-[34px] rounded-lg border border-gray-300 px-1 text-xs" value={settings[field]} onChange={event => setSettings({ ...settings, [field]: event.target.value })}>
                      {!choices.some(choice => choice.value === settings[field]) && <option value={settings[field]}>{settings[field]}</option>}
                      {choices.map(choice => <option key={choice.value} value={choice.value}>{choice.label}</option>)}
                    </select>
                  </label>
                ))}
                <label className="block min-w-0 whitespace-nowrap text-xs font-medium">
                  <span className="mb-2 block leading-5">{t('include_extended_hours', { defaultValue: 'Include Extended Hours' })}</span>
                  <span className="flex h-[34px] items-center">
                    <input type="checkbox" role="switch" aria-checked={settings.include_extended_hours} name="include_extended_hours" checked={settings.include_extended_hours} onChange={event => setSettings({ ...settings, include_extended_hours: event.target.checked })} className="peer sr-only" />
                    <span aria-hidden="true" className="relative h-6 w-11 shrink-0 cursor-pointer rounded-full bg-gray-300 transition-colors after:absolute after:left-0.5 after:top-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow after:transition-transform peer-checked:bg-blue-600 peer-checked:after:translate-x-5 peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500 peer-focus-visible:ring-offset-2 peer-disabled:cursor-not-allowed peer-disabled:opacity-50" />
                  </span>
                </label>
              </div>
              {incompatiblePeriod && <p className="text-sm text-red-700">{t('schedule_invalid_period', { defaultValue: 'One-minute candles require a period of seven days or less.' })}</p>}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  ['lookback', 'Lookback bars (blank = all)', 1, undefined, '1'],
                  ['account_size', 'Account size', 0.01, undefined, 'any'],
                  ['risk_pct', 'Risk per trade (%)', 0.01, 100, 'any'],
                  ['timeout', 'Data timeout (seconds)', 1, 30, '1'],
                  ['max_retries', 'Maximum retries', 1, 10, '1'],
                ].map(([field, label, min, max, step]) => (
                  <label key={field} className="block text-sm font-medium">
                    {t(`schedule_${field}`, { defaultValue: label })}
                    <input className={inputClass} type="number" min={min} max={max} step={step} required={field !== 'lookback'} value={settings[field] ?? ''} onChange={event => setSettings({ ...settings, [field]: event.target.value === '' ? null : Number(event.target.value) })} />
                  </label>
                ))}
              </div>
            </fieldset>
            {error && <p role="alert" tabIndex={0} className="max-h-48 overflow-y-auto overscroll-contain whitespace-pre-wrap break-words text-sm text-red-700">{error}</p>}
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-200 pt-4">
              <button type="button" className={`${actionButtonClass} mr-auto border-red-200 bg-white text-red-700 enabled:hover:border-red-300 enabled:hover:bg-red-50 focus-visible:ring-red-500`} disabled={busy || running} onClick={remove}>{t('schedule_delete', { defaultValue: 'Delete' })}</button>
              <button type="button" className={`${actionButtonClass} border-blue-200 bg-blue-50 text-blue-700 enabled:hover:border-blue-300 enabled:hover:bg-blue-100 focus-visible:ring-blue-500`} disabled={busy || running} onClick={() => { if (!busy && !running) perform(() => onRun(schedule.id)); }}>{t('schedule_run_now', { defaultValue: 'Run now' })}</button>
              {running && <button type="button" className={`${actionButtonClass} border-red-200 bg-white text-red-700 enabled:hover:bg-red-50 focus-visible:ring-red-500`} disabled={busy || cancelling} onClick={cancelRun}>{t('schedule_cancel_run', { defaultValue: 'Cancel run' })}</button>}
              <Dialog.Close asChild><button type="button" className={`${actionButtonClass} border-gray-300 bg-white text-gray-700 enabled:hover:bg-gray-50 focus-visible:ring-gray-500`} disabled={busy}>{t('cancel')}</button></Dialog.Close>
              <button type="submit" className={`${actionButtonClass} border-transparent bg-blue-600 text-white shadow-sm enabled:hover:bg-blue-700 focus-visible:ring-blue-500 disabled:bg-gray-400`} disabled={busy || running || !validSymbols || !validSettings || incompatiblePeriod || !isScheduleConfigurationValid(configuration)}>{t('schedule_update', { defaultValue: 'Update schedule' })}</button>
            </div>
          </form>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}





















